import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';

import {
  DetailsList,
  Selection,
  DetailsListLayoutMode,
} from 'office-ui-fabric-react/lib/DetailsList';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Link } from 'office-ui-fabric-react/lib/Link';

import { localizable } from 'components/Localization';
import OfferPreview from 'components/OfferPreview';
import Money from 'components/Money';

import RegistrationPanel from './RegistrationPanel';
import ContactPanel from './ContactPanel';
import BulkRegistrationCsvPanel from './BulkRegistration/BulkRegistrationCsvPanel';
import BulkRegistrationInventoryPanel from './BulkRegistration/BulkRegistrationInventoryPanel';
import RefuseDialog from './RefuseDialog';
import DevLinkOffer from './DevLinkOffer';
import SelfRegistrationApprovalDialog from './SelfRegistrationApprovalDialog';

import OfferNotificationsPanel from './OfferNotificationsPanel';
import AuctionFinalsPanel from '../../../components/AuctionFinals/AuctionFinalsPanel';

const statusToHidePreview = ['invited', 'submitted', 'refused'];

const byDefaultDoNothing = () => {};
const columns = (t, onClick = byDefaultDoNothing, hasPreviousValue) => {
  const previousValue = {
    key: 'previousRound',
    name: t('Previous round'),
    fieldName: 'previousRound',
    minWidth: 100,
    maxWidth: 150,
    onColumnClick: onClick,
    isSorted: false,
    isSortedDescending: false,
    isResizable: true,
  };

  const columns = [
    {
      key: 'preview',
      minWidth: 20,
      maxWidth: 20,
    },
    {
      key: 'name',
      name: t('Customer name'),
      fieldName: 'name',
      minWidth: 150,
      maxWidth: 400,
      onColumnClick: onClick,
      isSorted: true,
      isSortedDescending: false,
      isResizable: true,
    },
    {
      key: 'status',
      name: t('Status'),
      fieldName: 'status',
      minWidth: 100,
      maxWidth: 150,
      onColumnClick: onClick,
      isSorted: false,
      isSortedDescending: false,
      isResizable: true,
    },
    {
      key: 'invitedAt',
      name: t('Invited on'),
      fieldName: 'invitedAt',
      minWidth: 100,
      maxWidth: 150,
      onColumnClick: onClick,
      isSorted: false,
      isSortedDescending: false,
      isResizable: true,
    },
    {
      key: 'submittedAt',
      name: t('Submitted at'),
      fieldName: 'submittedAt',
      minWidth: 100,
      maxWidth: 150,
      onColumnClick: onClick,
      isSorted: false,
      isSortedDescending: false,
      isResizable: true,
    },
    {
      key: 'submittedValue',
      name: t('Submitted value'),
      fieldName: 'submittedValue',
      minWidth: 100,
      maxWidth: 150,
      onColumnClick: onClick,
      isSorted: false,
      isSortedDescending: false,
      isResizable: true,
    },
  ];
  if (hasPreviousValue) {
    columns.push(previousValue);
  }
  columns.push({
    key: 'actions',
    minWidth: 50,
  });
  return columns;
};

@inject('app', 'sale', 'user')
@localizable
@observer
class OffersSection extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
  };

  @observable offersColumns = [];

  @observable submitError;
  @observable dismissDisabled = false;

  constructor(props) {
    super(props);
    this.selection = new Selection({
      onSelectionChanged: this.handleSelectionChanged,
    });
  }

  componentDidMount() {
    const {
      store: {
        root: { round },
      },
      t,
    } = this.props;
    if (round.definition) {
      const hasPreviousValue = round.definition.number > 1;
      this.offersColumns = columns(
        t,
        this.handleColumnHeaderClick,
        hasPreviousValue
      );
    }
    var inviteStr = sessionStorage.getItem('invite');
    var hasInvitation = inviteStr && inviteStr.length > 0;
    if (!hasInvitation) {
      return;
    }

    var { sale } = this.props;
    var canInvite = sale.cmdBar.userRegistrationEnabled;
    if (canInvite) {
      const invite = JSON.parse(inviteStr);
      this.props.ui.currentPanel = invite.panel;
    } else {
      sessionStorage.removeItem('invite');
      sale.error = { code: 'registration_disabled' };
    }
  }

  componentWillReact() {
    const {
      store: {
        root: { round },
      },
      t,
    } = this.props;
    if (round.definition) {
      const hasPreviousValue = round.definition.number > 1;
      this.offersColumns = columns(
        t,
        this.handleColumnHeaderClick,
        hasPreviousValue
      );
    }
  }

  handleColumnHeaderClick = (e, c) => {
    const { t } = this.props;
    const field = c.fieldName;
    const index = this.offersColumns.findIndex((c) => c.key === field);
    const column = this.offersColumns[index];
    if (!column) {
      return;
    }
    const {
      store,
      store: { viewModel: vm = {} },
    } = this.props;
    if (!vm.offers || vm.offers.length === 0) {
      return;
    }
    switch (field) {
      case 'name':
        store.sortOffersByBuyerName(column.isSortedDescending);
        break;
      case 'submittedValue':
        vm.offers = vm.offers.sortBy(
          field,
          column.isSortedDescending,
          (v) => v || 0
        );
        break;
      case 'status':
        vm.offers = vm.offers.sortBy(field, column.isSortedDescending, (v) =>
          t(v)
        );
        break;
      default:
        vm.offers = vm.offers.sortBy(field, column.isSortedDescending);
        break;
    }
    this.offersColumns = this.offersColumns.map((c, i) => {
      if (i !== index) {
        c.isSorted = false;
        c.isSortedDescending = false;
      } else {
        c.isSorted = true;
        c.isSortedDescending = !c.isSortedDescending;
      }
      return c;
    });
  };

  handleRenderItemColumn = (item, index, column) => {
    const { t, ui, sale, user } = this.props;
    const content = item[column.fieldName];
    const hasPermissions = sale.user.hasPermissions(
      item.agentData,
      user.roleDefinitions.readonly
    );
    switch (column.key) {
      case 'name':
        return (
          <React.Fragment>
            {this.renderBuyerName(item, ui, t)}

            {item.failedChannels && item.failedChannels.includes('email') && (
              <Link onClick={() => ui.offerNotifications()}>
                <Icon
                  iconName="MailAlert"
                  title={t('email_failed')}
                  style={{ color: 'red', paddingLeft: '5px' }}
                />
              </Link>
            )}

            {item.failedChannels && item.failedChannels.includes('sms') && (
              <Link onClick={() => ui.offerNotifications()}>
                <Icon
                  iconName="Message"
                  title={t('sms_failed')}
                  style={{ color: 'red', paddingLeft: '5px' }}
                />
              </Link>
            )}
          </React.Fragment>
        );

      case 'submittedAt':
        const {
          store: {
            root: { round },
          },
        } = this.props;
        if (
          round.isOpen &&
          item.status !== 'confirmed' &&
          item.confirmedOffer &&
          item.confirmedOffer.submittedAt
        ) {
          return (
            <span>
              {item.confirmedOffer.submittedAt.toFormatedDateString('ll LTS')}
            </span>
          );
        }
        return (
          <span>{content ? content.toFormatedDateString('ll LTS') : ''}</span>
        );
      case 'invitedAt':
      case 'expireAt':
        return <span>{content ? content.toFormatedDateString('ll') : ''}</span>;
      case 'submittedValue': {
        const {
          store: {
            root: { round },
          },
        } = this.props;
        if (round.type === 'registration') return '';
        if (round.isOpen && item.status !== 'confirmed') {
          if (
            item.data &&
            item.data.offer_gross_value &&
            item.confirmedOffer &&
            item.confirmedOffer.status === 'confirmed'
          ) {
            return (
              <Money
                value={item.data.offer_gross_value}
                currency={this.props.app.currency.code}
                locale={this.props.intl.locale}
              />
            );
          }
          return <span />;
        }
        return (
          <span
            style={{ color: item.redButtonPriceFlag ? '#dc1c2e' : 'inherit' }}
          >
            {content < 0 && t('hidden')}
            {content >= 0 && (
              <Money
                value={content}
                currency={this.props.app.currency.code}
                locale={this.props.intl.locale}
              />
            )}
          </span>
        );
      }
      case 'previousRound': {
        const {
          store: {
            root: { round },
          },
        } = this.props;
        const lastValues = item.previousRounds || {};
        let lastRound = Math.max(...Object.keys(lastValues));

        if (!round.isOpen) {
          const rounds = Object.keys(lastValues) || [];
          lastRound = rounds[rounds.length - 2] || 1;
        }
        if (lastRound < 1) {
          return <span />;
        }

        return (
          <span
            style={{ color: item.redButtonPriceFlag ? '#dc1c2e' : 'inherit' }}
          >
            <Money
              value={lastValues[lastRound]}
              defaultValue={content === 'hidden' ? t(content) : content}
              currency={this.props.app.currency.code}
              locale={this.props.intl.locale}
            />
          </span>
        );
      }
      case 'status':
        return (
          <span className={'offerstatus-' + content}>
            {t(`offerstatus::${content}`)}
          </span>
        );
      case 'preview':
        if (
          statusToHidePreview.some((s) => s === item.status) ||
          !item.isValid ||
          !hasPermissions
        ) {
          return null;
        }
        return (
          <Link onClick={() => ui.showOfferPreview()}>
            <Icon iconName="Preview" />
          </Link>
        );
      case 'actions':
        return (
          <div style={{ textAlign: 'right' }}>
            {hasPermissions ? (
              <Link onClick={() => ui.offerNotifications()}>
                <Icon iconName="MailForward" />
              </Link>
            ) : null}
            &nbsp;&nbsp;
            <DevLinkOffer item={item} />
          </div>
        );
      default:
        return <span>{content}</span>;
    }
  };

  renderBuyerName = (offer, ui, t) => {
    if (
      !offer.buyer.buyer_lastName &&
      !offer.buyer.buyer_firstName &&
      !offer.buyer.buyer_company_name
    ) {
      if (offer.agentData) {
        if (offer.agentData.buyer_reference)
          return this.buyerName(
            t(`Customer {buyer_reference} of {name}`, offer.agentData),
            offer
          );
        return this.buyerName(t(`Customer of {name}`, offer.agentData), offer);
      }
      return this.buyerName(t('Customer'), offer);
    }

    const names = [];
    names.push(`${offer.buyer.buyer_firstName} ${offer.buyer.buyer_lastName}`);
    if (offer.buyer.buyer_type === 'company')
      names.push(offer.buyer.buyer_company_name);
    if (offer.buyer.others)
      offer.buyer.others.forEach((buyer2) => {
        names.push(`${buyer2.buyer_firstName} ${buyer2.buyer_lastName}`);
      });
    const name = names.join(', ');
    return this.buyerName(name, offer, () => ui.editOffer());
  };

  buyerName = (content, offer, onClick) => {
    return (
      <span
        onClick={onClick}
        style={{
          cursor: onClick ? 'pointer' : 'inherit',
          textDecoration: offer.buyerIsBlocked ? 'line-through' : 'underline',
        }}
      >
        {content}
      </span>
    );
  };

  handleSelectionChanged = () => {
    this.props.store.select(this.selection.getSelection()[0]);
  };

  handleClosePanel = () => {
    if (!this.dismissDisabled) this.props.ui.closePanel();
  };

  handleRegistration = (reg) => {
    this.submitError = null;
    this.props.store.register(reg).then(
      () => {
        this.handleClosePanel();
      },
      (error) => {
        if (
          error &&
          error.response &&
          error.response.result &&
          !error.response.result.successful &&
          error.response.result.error
        ) {
          this.submitError = {
            data: reg,
            error: error.response.result.error,
          };
        } else {
          this.props.sale.failed(error);
          this.handleClosePanel();
        }
      }
    );
  };

  handleUpdateOffer = (offer) => {
    this.submitError = null;
    this.props.store.updateBuyer(offer).then(
      () => {
        this.handleClosePanel();
      },
      (error) => {
        if (
          error &&
          error.response &&
          error.response.result &&
          !error.response.result.successful &&
          error.response.result.error
        ) {
          this.submitError = {
            data: offer,
            error: error.response.result.error,
          };
        } else {
          this.props.sale.failed(error);
          this.handleClosePanel();
        }
      }
    );
  };

  handleUpdateContact = (contact) => {
    this.submitError = null;
    this.props.store.updateContact(contact).then(
      () => {
        this.handleClosePanel();
      },
      (error) => {
        if (
          error &&
          error.response &&
          error.response.result &&
          !error.response.result.successful &&
          error.response.result.error
        ) {
          this.submitError = {
            data: contact,
            error: error.response.result.error,
          };
        } else {
          this.props.sale.failed(error);
          this.handleClosePanel();
        }
      }
    );
  };

  handleUploadOffer = (offer) => {
    this.submitError = null;
    this.props.store.uploadOffer(offer).then(
      () => {
        this.handleClosePanel();
      },
      (error) => {
        if (
          error &&
          error.response &&
          error.response.result &&
          !error.response.result.successful &&
          error.response.result.error
        ) {
          this.submitError = {
            data: offer,
            error: error.response.result.error,
          };
        } else {
          this.props.sale.failed(error);
          this.handleClosePanel();
        }
      }
    );
  };

  handleIncreaseOffer = (offer) => {
    this.props.store.increaseOffer(offer);
    this.handleClosePanel();
  };

  handleRefuseOffer = (reason, offer) => {
    this.props.store.refuse(reason, offer);
    this.handleClosePanel();
  };

  handleApproveRegistration = ({ offerData }) => {
    this.props.store.approveRegistration(offerData);
    this.handleClosePanel();
  };

  render() {
    const {
      ui,
      store: { viewModel: vm = {}, selectedOffer: selected },
      t,
    } = this.props;
    const {
      store: {
        root: { saleEdit, round },
      },
    } = this.props;

    // if (!vm.isEnabled) {
    //   return <span />;
    // }

    return (
      <div>
        {vm.offers && vm.offers.length > 0 && (
          <DetailsList
            columns={this.offersColumns}
            items={vm.offers || []}
            onRenderItemColumn={this.handleRenderItemColumn}
            selectionMode={1}
            checkboxVisibility={0}
            selection={this.selection}
            selectionPreservedOnEmptyClick={true}
            layoutMode={DetailsListLayoutMode.justified}
          />
        )}

        {ui.currentPanel === 'registration' &&
          this.renderRegistrationPanel({
            sale: saleEdit,
            onSave: this.handleRegistration,
          })}

        {ui.currentPanel === 'bulkRegistrationCsv' &&
          this.renderBulkRegistrationCsvPanel({
            sale: saleEdit,
          })}

        {ui.currentPanel === 'bulkRegistrationOnOffice' &&
          this.renderBulkRegistrationInventoryPanel({
            sale: saleEdit,
            title: t('OnOffice Import'),
          })}

        {ui.currentPanel === 'bulkRegistrationPropUp' &&
          this.renderBulkRegistrationInventoryPanel({
            sale: saleEdit,
            title: t('PropUp Import'),
          })}

        {ui.currentPanel === 'bulkRegistrationPropstack' &&
          this.renderBulkRegistrationInventoryPanel({
            sale: saleEdit,
            title: t('Propstack Import'),
          })}

        {ui.currentPanel === 'bulkRegistrationCrm' &&
          this.renderBulkRegistrationInventoryPanel({
            sale: saleEdit,
            title: t('CRM Import'),
          })}

        {ui.currentPanel === 'editOffer' &&
          !!selected &&
          this.renderRegistrationPanel({
            sale: saleEdit,
            onSave: this.handleUpdateOffer,
            offer: selected,
          })}

        {ui.currentPanel === 'updateContact' &&
          !!selected &&
          this.renderUpdateContact({
            sale: saleEdit,
            onSave: this.handleUpdateContact,
            offer: selected,
          })}

        {ui.currentPanel === 'registerOnBehalf' &&
          this.renderRegistrationPanel({
            sale: saleEdit,
            round: round,
            onSave: (o) => {
              this.dismissDisabled = true;
              ui.prompt.open({
                title:
                  !!selected && selected.isValid
                    ? t('Increase offer on behalf of the customer')
                    : t('Upload offer on behalf of a customer'),
                content: 'upload',
                action: () => {
                  this.dismissDisabled = false;
                  if (!!selected && selected.isValid)
                    this.handleIncreaseOffer(o);
                  else this.handleUploadOffer(o);
                },
              });
            },
            mode: !!selected && selected.isValid ? 'increase' : 'upload',
            offer: selected,
          })}

        {ui.currentPanel === 'registrationApproval' && (
          <SelfRegistrationApprovalDialog
            sale={saleEdit}
            offer={selected}
            onSubmit={this.handleApproveRegistration}
            onClose={this.handleClosePanel}
          />
        )}

        {ui.currentPanel === 'refuse' && (
          <RefuseDialog
            sale={saleEdit}
            offer={selected}
            onSubmit={this.handleRefuseOffer}
            onClose={this.handleClosePanel}
          />
        )}

        {ui.currentPanel === 'offerPreview' && selected && (
          <OfferPreview
            closePanel={() => ui.closePanel()}
            // selectedOffer={selected}
            // url={`/api/v1/offers/${selected.saleId}/${selected.buyerId}/preview?locale={user.claims.locale}`}
            data={{
              template: `/api/v1/offers/{saleId}/{offerId}/preview`,
              saleId: selected.saleId,
              offerId: selected.buyerId,
            }}
          />
        )}

        {ui.currentPanel === 'offerNotifications' && !!selected && (
          <OfferNotificationsPanel
            sale={saleEdit}
            offer={selected}
            isOpen={true}
            onClose={this.handleClosePanel}
          />
        )}

        {ui.currentPanel === 'auctionFinals' && (
          <AuctionFinalsPanel
            sale={saleEdit.root}
            onClose={this.handleClosePanel}
          />
        )}
      </div>
    );
  }

  renderRegistrationPanel({
    sale,
    round,
    onSave,
    offer = undefined,
    mode = 'read',
  }) {
    const hasPermissions = this.props.sale.hasOfferPermissions;
    return (
      <RegistrationPanel
        sale={sale}
        round={round}
        offer={offer}
        mode={mode}
        isOpen={true}
        onSave={onSave}
        onClose={this.handleClosePanel}
        usedEmails={this.props.store.emails}
        submitError={this.submitError}
        hasPermissions={hasPermissions}
      />
    );
  }

  renderUpdateContact({ sale, onSave, offer = undefined }) {
    const hasPermissions = this.props.sale.hasOfferPermissions;
    return (
      <ContactPanel
        sale={sale}
        offer={offer}
        isOpen={true}
        onSave={onSave}
        onClose={this.handleClosePanel}
        usedEmails={this.props.store.emails}
        submitError={this.submitError}
        hasPermissions={hasPermissions}
      />
    );
  }

  renderBulkRegistrationCsvPanel({
    sale,
    round,
    offer = undefined,
    mode = 'read',
  }) {
    return (
      <BulkRegistrationCsvPanel
        sale={sale}
        round={round}
        offer={offer}
        mode={mode}
        isOpen={true}
        onClose={this.handleClosePanel}
        usedEmails={this.props.store.emails}
        submitError={this.submitError}
      />
    );
  }

  renderBulkRegistrationInventoryPanel({
    sale,
    round,
    offer = undefined,
    mode = 'read',
    title = '',
  }) {
    return (
      <BulkRegistrationInventoryPanel
        sale={sale}
        round={round}
        offer={offer}
        mode={mode}
        isOpen={true}
        title={title}
        onClose={this.handleClosePanel}
        usedEmails={this.props.store.emails}
        submitError={this.submitError}
      />
    );
  }
  /*
  renderBulkRegistrationPropUpPanel({
    sale,
    round,
    offer = undefined,
    mode = 'read',
  }) {
    return (
      <BulkRegistrationPropUpPanel
        sale={sale}
        round={round}
        offer={offer}
        mode={mode}
        isOpen={true}
        onClose={this.handleClosePanel}
        usedEmails={this.props.store.emails}
        submitError={this.submitError}
      />
    );
  }
  */
}

export default OffersSection;



// WEBPACK FOOTER //
// ./src/app/containers/Sale/OffersSection/index.jsx